'use client';

import get from 'ts-get';

import { useRutilusQuery } from '../../../api/graphql/useRutilusQuery';
import { reportMykissClientError } from '../../../helpers/errorHandling/client';
import { useClientHasRendered } from '../../../helpers/hooks';
import { getPagePosts } from '../../../queries/rutilus/Page';

import { Heading } from '../../../components/Heading/Heading';
import { PostsMasonry } from '../../../components/PostsMasonry/PostsMasonry';
import { Post } from '../../../components/PostsViewer/types/Post';
import { getNodesFromConnection } from '../../../helpers/graphql';
import { GetPagePosts, GetPagePostsVariables } from '../../../interfaces/graphql';

interface IProps {
  pageId: string;
  pageName: string;
}

export const PageFeed = ({ pageId, pageName }: IProps) => {
  const clientHasRendered = useClientHasRendered();

  const { data, loading, fetchMore } = useRutilusQuery<GetPagePosts, GetPagePostsVariables>(
    getPagePosts,
    {
      variables: { id: pageId },
    },
  );

  const posts = getNodesFromConnection<Post>(data?.entry.posts);
  const hasMorePosts = get(data, d => d.entry.posts.pageInfo.hasNextPage, false);
  const endCursor = get(data, d => d.entry.posts.pageInfo.endCursor, null);

  const fetchNextPage = async (): Promise<unknown> =>
    fetchMore({
      variables: { id: pageId, after: endCursor },
      updateQuery: (prev, { fetchMoreResult }): GetPagePosts => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          entry: {
            ...fetchMoreResult.entry,
            posts: {
              ...fetchMoreResult.entry.posts,
              edges: [
                ...get(prev, p => p.entry.posts.edges, []),
                ...get(fetchMoreResult, r => r.entry.posts.edges, []),
              ],
            },
          },
        };
      },
    }).catch(reportMykissClientError);

  if (!loading && clientHasRendered && posts.length === 0) {
    return <></>;
  }

  return (
    <>
      <Heading variant="sm">Posts</Heading>
      <PostsMasonry
        isLoading={clientHasRendered && loading}
        posts={posts}
        fetchMore={fetchNextPage}
        hasMore={hasMorePosts}
        appDownloadModalHeader={`See all catches and posts from ${pageName} in the Fishbrain app`}
        appDownloadModalText="Download the app to see more info about your favorite anglers, ability to follow them and see what they catch next."
      />
    </>
  );
};
